import { Card, Divider, Pagination, Tag, Table, Button, Popconfirm } from 'antd';
import moment from 'moment';
import { useEffect } from 'react';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import SearchFilter from '../../component/Filter';
import { useAppDispatch, useAppSelector } from '../../methods/redux/hooks';
import { retrieveAegisThreats, retrieveAegisStatus, resolveAegisThreat, resolveAllAegisThreats } from '../../methods/redux/reducers/compliance';
import remakeQueryString from '../../methods/utils/remake-query-string';

const Container = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;
	gap: 1em;
`;

const AegisThreats = () => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { aegisThreats, aegisStatus } = useAppSelector((store) => store.compliance);
	const [searchParams, setSearchParams] = useSearchParams();

	useEffect(() => {
		dispatch(retrieveAegisStatus());
	}, []);

	useEffect(() => {
		const queryData = Object.fromEntries(searchParams.entries());
		if (Object.keys(queryData).length > 0) {
			dispatch(retrieveAegisThreats(queryData));
		}

		if (Object.keys(queryData).length === 0) {
			setSearchParams({
				pageId: '1',
				perPage: '20',
				sort: 'desc',
			});
		}
	}, [searchParams]);

	const updateQueryString = (query: any, action = 'update') => {
		remakeQueryString(query, searchParams, setSearchParams, action);
	};

	const handleResolve = async (threatId: string) => {
		const success = await resolveAegisThreat(threatId);
		if (success) {
			const queryData = Object.fromEntries(searchParams.entries());
			dispatch(retrieveAegisThreats(queryData));
			dispatch(retrieveAegisStatus());
		}
	};

	const handleResolveAll = async () => {
		const success = await resolveAllAegisThreats();
		if (success) {
			const queryData = Object.fromEntries(searchParams.entries());
			dispatch(retrieveAegisThreats(queryData));
			dispatch(retrieveAegisStatus());
		}
	};

	const columns = [
		{
			title: 'ID',
			dataIndex: '_id',
			key: '_id',
			render: (id: string) => <small>{id}</small>,
		},
		{
			title: 'Threat Type',
			dataIndex: 'threatType',
			key: 'threatType',
			render: (threatType: string) => <Tag color="blue">{threatType || 'N/A'}</Tag>,
		},
		{
			title: 'Severity',
			dataIndex: 'severity',
			key: 'severity',
			render: (severity: string) => {
				let color = 'default';
				if (severity === 'low') color = 'green';
				if (severity === 'medium') color = 'orange';
				if (severity === 'high') color = 'red';
				if (severity === 'critical') color = 'purple';
				return <Tag color={color}>{severity?.toUpperCase() || 'N/A'}</Tag>;
			},
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			render: (status: string) => (
				<Tag color={status === 'active' ? 'red' : 'green'}>
					{status?.toUpperCase() || 'N/A'}
				</Tag>
			),
		},
		{
			title: 'Ip Address',
			dataIndex: 'ipAddress',
			key: 'ipAddress',
		},
		{
			title: 'Date Created',
			dataIndex: 'createdAt',
			key: 'createdAt',
			render: (date: string) => moment(date).format('MMM DD, YYYY / hh:mm a'),
		},
		{
			title: 'Action',
			key: 'action',
			render: (record: any) => (
				<Popconfirm
					title="Are you sure you want to resolve this threat?"
					onConfirm={() => handleResolve(record._id)}
					okText="Yes"
					cancelText="No"
					disabled={record.status === 'RESOLVED'}
				>
					<Button 
						type="primary" 
						size="small" 
						disabled={record.status === 'RESOLVED'}
					>
						Resolve
					</Button>
				</Popconfirm>
			),
		},
	];

	return (
		<Container>
			<Card>
				<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<div style={{ display: 'flex', alignItems: 'center', gap: '1em' }}>
						<Button 
							type="text" 
							icon={<ArrowLeftOutlined />} 
							onClick={() => navigate(-1)} 
							style={{ fontSize: '18px' }}
						/>
						<div>
							<h1>Aegis Security Monitoring</h1>
							<p>Real-time security threat detection and vulnerability management.</p>
						</div>
					</div>
					<Popconfirm
						title="Are you sure you want to resolve all active threats?"
						onConfirm={handleResolveAll}
						okText="Yes"
						cancelText="No"
						disabled={aegisThreats.records?.length === 0}
					>
						<Button 
							type="primary" 
							danger 
							disabled={aegisThreats.records?.length === 0}
						>
							Resolve All Threats
						</Button>
					</Popconfirm>
				</div>
				<Divider />

				<div style={{ display: 'flex', gap: '2em', marginBottom: '1em' }}>
					<Card size="small" title="Status">
						<Tag color={aegisStatus.status === 'active' ? 'green' : 'red'}>
							{aegisStatus.status?.toUpperCase() || 'UNKNOWN'}
						</Tag>
					</Card>
					<Card size="small" title="Active Threats">
						<strong>{aegisStatus.counters?.activeThreats || 0}</strong>
					</Card>
					<Card size="small" title="Critical Threats">
						<strong>{aegisStatus.counters?.criticalThreats || 0}</strong>
					</Card>
					<Card size="small" title="Threats Today">
						<strong>{aegisStatus.counters?.threatsToday || 0}</strong>
					</Card>
					<Card size="small" title="Total Vulnerabilities">
						<strong>{aegisStatus.counters?.totalVulnerabilities || 0}</strong>
					</Card>
				</div>

				<Divider />

				<SearchFilter
					title="Filter Threats"
					searchFilters={[
						{
							label: 'Severity',
							name: 'severity',
							htmlElement: 'select',
							selectOptions: [
								{ value: 'low', name: 'Low' },
								{ value: 'medium', name: 'Medium' },
								{ value: 'high', name: 'High' },
								{ value: 'critical', name: 'Critical' },
							],
						},
						{
							label: 'Status',
							name: 'status',
							htmlElement: 'select',
							selectOptions: [
								{ value: 'active', name: 'Active' },
								{ value: 'resolved', name: 'Resolved' },
								{ value: 'ignored', name: 'Ignored' },
							],
						},
						{
							label: 'Threat Type',
							name: 'type',
							htmlElement: 'input',
						},
					]}
					retrieveQueryString={updateQueryString}
				/>

				<Divider />

				<Table
					columns={columns}
					dataSource={aegisThreats.records}
					rowKey="_id"
					pagination={false}
					loading={!aegisThreats.records && aegisThreats?.totalRecords === 0}
				/>

				<Divider />

				<Pagination
					onChange={(pageId) => updateQueryString({ pageId })}
					current={aegisThreats?.currentPage || 1}
					total={aegisThreats?.totalRecords || 0}
					pageSize={aegisThreats?.pageSize || 20}
					showSizeChanger
					onShowSizeChange={(_, size) => updateQueryString({ perPage: size })}
				/>
			</Card>
		</Container>
	);
};

export default AegisThreats;
